import React, { useEffect, useState } from "react";
import ReactList, {
  ReactListEmpty,
  ReactListInitialLoader,
  ReactListItems,
  ReactListProvider,
  ReactListSearch,
} from "@7span/react-list";

import { Badge } from "ui/components/badge";
import { EMPLOYEE_FILTERS_FORM } from "components/employee/constants";
import { EmptySection1 } from "components/empty";
import { EvtEmployeeCard } from "components/employee/display-card";
import { EvtEmployeePlaceholderCard } from "components/employee/placeholder";
import EvtSheet from "components/sheet";
import { Icon } from "components/index";
import { RenderProfile } from "components/render-profile";
import Search from "components/list/search";
import { Shimmer } from "components/shimmer";
import { INFO, X } from "icons/src";
import { fetchListData } from "components/employee/api";
import classNames from "classnames";

const attrs = [
  "id",
  "first_name",
  "last_name",
  "avatar",
  "department.name",
  "user.role.name",
  "grade",
  "title",
];

const EmployeeInput = ({
  isOpen = false,
  onChange,
  value,
  set = "all",
  disabled = false,
  multiselect = false,
  singleSelect = false,
  allowRemoval = false,
  footer,
  children,
  excludedEmployeeIds = [],
  filters = {},
  className,
  info,
  enableStateManager = true,
}) => {
  const [open, setOpen] = useState(isOpen);
  const [selectedEmployees, setSelectedEmployees] = useState([]);

  useEffect(() => {
    if (value) {
      setSelectedEmployees(Array.isArray(value) ? value : [value]);
    }
  }, [value]);

  function handleSelectEmployee(employee) {
    if (multiselect) {
      const newSelection = [...selectedEmployees, employee];
      setSelectedEmployees(newSelection);
      onChange?.(newSelection);
    } else if (singleSelect) {
      const newSelection = [employee];
      setSelectedEmployees(newSelection);
      onChange?.(employee);
    } else {
      setSelectedEmployees([employee]);
      onChange?.(employee);
      setOpen(false);
    }
  }

  function handleRemoveEmployee(employeeId) {
    const newSelection = selectedEmployees.filter((e) => e.id !== employeeId);
    setSelectedEmployees(newSelection);
    if (singleSelect) {
      onChange?.(null);
    } else {
      onChange?.(newSelection);
    }
  }
  const defaultfilter = {
    ...EMPLOYEE_FILTERS_FORM[set],
    ...filters,
  };

  return (
    <EvtSheet
      setOpen={setOpen}
      open={open}
      title="Select Employee"
      disabled={disabled}
      trigger={
        multiselect ? (
          children ? (
            children
          ) : (
            <div className="flex flex-wrap flex-1 gap-2 w-full">
              {selectedEmployees.map((employee) => (
                <Badge key={employee.id} variant="outline">
                  {employee.first_name} {employee.last_name}
                </Badge>
              ))}
              {selectedEmployees.length === 0 && <EvtEmployeePlaceholderCard />}
            </div>
          )
        ) : singleSelect ? (
          children ? (
            children
          ) : (
            <div className="flex flex-wrap flex-1 gap-2 w-full">
              {selectedEmployees.map((employee) => (
                <Badge key={employee.id} variant="outline">
                  {employee.first_name} {employee.last_name}
                </Badge>
              ))}
              {selectedEmployees.length === 0 && <EvtEmployeePlaceholderCard />}
            </div>
          )
        ) : selectedEmployees.length > 0 ? (
          <EvtEmployeeCard
            user={selectedEmployees[0]}
            disabled={disabled}
            className={classNames(
              "hover:bg-neutral-50 cursor-pointer",
              className,
            )}
          />
        ) : children ? (
          children
        ) : (
          <EvtEmployeePlaceholderCard />
        )
      }
      footer={
        typeof footer === "function"
          ? footer({
            setOpen,
            selectedEmployees,
            setSelectedEmployees,
            setSelectedEmployee: (val) => {
              setSelectedEmployees(val ? [val] : []);
            },
          })
          : footer
      }
    >
      <ReactListProvider config={{ requestHandler: fetchListData }}>
        <ReactList
          filters={defaultfilter}
          perPage={10}
          meta={{ attrs }}
          sortBy="employee_id"
          sortOrder="asc"
          enableStateManager={enableStateManager}
          storageKey={enableStateManager ? set + "5" : undefined}
          attrs={attrs}
        >
          {({ filters, setFilters, refresh }) => {
            useEffect(() => {
              if (multiselect || singleSelect) {
                const allExcludedIds = [
                  ...selectedEmployees.map((e) => e.id),
                  ...excludedEmployeeIds,
                ];

                if (allExcludedIds.length > 0) {
                  // Build from defaults to avoid carrying any prior id filter from persisted state
                  const nextFilters = {
                    ...defaultfilter,
                    id: { _nin: allExcludedIds },
                  };
                  setFilters(nextFilters);
                } else {
                  // Reset to defaults when no exclusions
                  setFilters(defaultfilter);
                  refresh();
                }
              }
            }, [
              multiselect,
              singleSelect,
              selectedEmployees,
              excludedEmployeeIds,
            ]);

            return (
              <>
                <ReactListSearch>
                  {(props) => (
                    <Search
                      {...props}
                      searchPlaceholder={"Search"}
                      fluid={true}
                    />
                  )}
                </ReactListSearch>
                <div className="flex mt-2 flex-wrap gap-2">
                  {(multiselect || singleSelect) &&
                    selectedEmployees.map((employee) => (
                      <Badge key={employee.id} variant="outline">
                        <RenderProfile user={employee} className="size-6" />
                        {allowRemoval && (
                          <div
                            as="button"
                            onClick={() => {
                              handleRemoveEmployee(employee.id);
                              refresh();
                            }}
                          >
                            <Icon
                              icon={X}
                              className="ml-2 h-4 w-4 cursor-pointer "
                            />
                          </div>
                        )}
                      </Badge>
                    ))}
                </div>
                {info && (
                  <p className="text-xs text-muted-foreground">
                    <Icon icon={INFO} className="size-3 inline" /> {info}
                  </p>
                )}
                <div className="mb-3 mt-6 ">
                  <span className="text-muted-foreground text-sm">
                    Search Results:
                  </span>
                </div>

                <div className="overflow-y-auto flex-1 ">
                  <ReactListInitialLoader>
                    <div className="space-y-3">
                      {Array.from({ length: 8 }).map((_, index) => (
                        <Shimmer key={index} className={"w-full h-28"} />
                      ))}
                    </div>
                  </ReactListInitialLoader>
                  <ReactListEmpty>
                    <EmptySection1 />
                  </ReactListEmpty>
                  <ReactListItems>
                    {({ items }) => {
                      return (
                        <div className="space-y-3">
                          {items.map((user) => (
                            <EvtEmployeeCard
                              user={user}
                              onClick={() => handleSelectEmployee(user)}
                              className="hover:bg-neutral-50 cursor-pointer"
                            />
                          ))}
                        </div>
                      );
                    }}
                  </ReactListItems>
                </div>
              </>
            );
          }}
        </ReactList>
      </ReactListProvider>
    </EvtSheet>
  );
};

export default React.memo(EmployeeInput);
